import React, { useState } from "react";
import { useQuery } from "@apollo/client/react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import moment from "moment";

import {
  ASSET_REPLAY_LOGS_QUERY,
  CURRENT_KIT_RESERVATIONS_QUERY,
} from "../../constants/graphql/queries";
import { serverFormatWithTimezone } from "../../utils/dates";

const PAGE_SIZE = 20;
const DATE_FORMAT = "MM/DD/yyyy hh:mm A";
const RESERVED_ON_DATE_FORMAT = "MM/DD/YYYY";

function RackLocations({ locations }) {
  return locations
    ?.flatMap(location =>
      (location.warehouse_locations || [])
        .map(warehouseLocation => warehouseLocation.location_name)
        .filter(Boolean)
        .map(name => `${name} (${location.sub_qty})`)
    )
    .join(", ") || "N/A";
}

export default function KitInventoryActivity({ assetId }) {
  const [selected, setSelected] = useState("current_reservations");
  const isReservations = selected === "current_reservations";
  const reservationsQuery = useQuery(CURRENT_KIT_RESERVATIONS_QUERY, {
    variables: { assetId, limit: PAGE_SIZE },
    fetchPolicy: "network-only",
    skip: !isReservations,
  });
  const deploymentsQuery = useQuery(ASSET_REPLAY_LOGS_QUERY, {
    variables: { assetId, page: 1, limit: PAGE_SIZE, actionType: "picked" },
    fetchPolicy: "network-only",
    skip: isReservations,
  });

  const reservations = reservationsQuery.data?.currentKitReservations?.collection || [];
  const deployments = deploymentsQuery.data?.assetReplayLogs?.collection || [];
  const loading = isReservations
    ? reservationsQuery.loading
    : deploymentsQuery.loading;
  const records = isReservations ? reservations : deployments;

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Box>
          <Typography fontWeight={500} fontSize={18}>
            Kit Replay Log
          </Typography>
          {!loading && (
            <Typography color="text.secondary" fontSize={14} mt={0.5}>
              {records.length} Records
            </Typography>
          )}
        </Box>
        <FormControl size="small">
          <Select
            value={selected}
            onChange={event => setSelected(event.target.value)}
            sx={{ minWidth: 210, borderRadius: 2 }}
          >
            <MenuItem value="current_reservations">Current Reservations</MenuItem>
            <MenuItem value="recent_deployments">Outbound Deployments</MenuItem>
          </Select>
        </FormControl>
        {loading && (
          <Box ml={1} display="flex">
            <CircularProgress size={35} />
          </Box>
        )}
      </Box>
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: "0 1px 4px #0000001f" }}>
        {isReservations ? (
          <Table aria-label="Current kit reservations">
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ fontWeight: 500 }}>Reserved On</TableCell>
                <TableCell align="center" sx={{ fontWeight: 500 }}>Reserved For</TableCell>
                <TableCell align="center" sx={{ fontWeight: 500 }}>Reserved Quantity</TableCell>
                <TableCell align="center" sx={{ fontWeight: 500 }}>Reservation Number</TableCell>
                <TableCell align="center" sx={{ fontWeight: 500 }}>Reservation Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reservations.map(item => (
                <TableRow key={item.id} hover>
                  <TableCell align="center">
                    {moment(item.inventoryRequest.requestedAt, serverFormatWithTimezone).format(
                      RESERVED_ON_DATE_FORMAT
                    )}
                  </TableCell>
                  <TableCell align="center">{item.inventoryRequest.designer}</TableCell>
                  <TableCell align="center">{item.quantity}</TableCell>
                  <TableCell align="center">{item.inventoryRequest.workOrderNumber}</TableCell>
                  <TableCell align="center">{item.inventoryRequest.comment}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Table aria-label="Recent kit deployments">
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ fontWeight: 500 }}>Date</TableCell>
                <TableCell align="center" sx={{ fontWeight: 500 }}>Performed By</TableCell>
                <TableCell align="center" sx={{ fontWeight: 500 }}>Quantity Requested</TableCell>
                <TableCell align="center" sx={{ fontWeight: 500 }}>Order Number</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deployments.map(deployment => (
                <TableRow key={deployment.id} hover>
                  <TableCell align="center">{moment(deployment.performedAt).format(DATE_FORMAT)}</TableCell>
                  <TableCell align="center">{deployment.user?.email}</TableCell>
                  <TableCell align="center">{deployment.quantity}</TableCell>
                  <TableCell align="center">{deployment.woNumber}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      {!loading && records.length === 0 && (
        <Box display="flex" justifyContent="center" mt={2}>
          Empty
        </Box>
      )}
    </Box>
  );
}
